import { ApiEndpoint, ApiRegistry } from '@/api/registry';
import { clearAuthData } from '@/auth/authUtils';

const API_ROOT = '/api';

type QueryPrimitive = string | number | boolean | undefined | null;
interface QueryObject {
  [key: string]: QueryValue;
}
type QueryValue = QueryPrimitive | QueryValue[] | QueryObject;
type Query = Record<string, QueryValue>;
enum Method {
  Get = 'GET',
  Post = 'POST',
  Put = 'PUT',
  Delete = 'DELETE',
}

export type FetcherOptions = {
  query?: Query;
  body?: unknown;
  headers?: Record<string, string>;
  method?: Method;
  pathParams?: Record<string, string | number>;
};

export class ApiHttpError extends Error {
  status: number;
  statusText: string;
  data?: unknown;

  constructor(status: number, message: string, statusText: string, data?: unknown) {
    super(message);
    this.name = 'ApiHttpError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

export function applyPathParams(path: string, params?: Record<string, string | number>): string {
  if (!params) return path;

  return path.replace(/:([A-Za-z0-9_]+)/g, (_, key) => {
    const value = params[key];
    if (value === undefined) {
      throw new Error(`Missing path param "${key}" for path "${path}"`);
    }
    return encodeURIComponent(String(value));
  });
}

export async function apiFetch<T = unknown>(tenantAlias: string, endpoint: ApiEndpoint, opts: FetcherOptions = {}) {
  const { query, body, headers } = opts;
  const { method, path: rawPath, tenantHeader = 'X-API-Tenant-Alias' } = ApiRegistry[endpoint];
  const path = applyPathParams(rawPath, opts.pathParams);

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    [tenantHeader]: tenantAlias,
  };

  const toQs = (query?: Query) => {
    if (!query) return '';
    const usp = new URLSearchParams();
    const appendQueryParam = (key: string, value: QueryValue) => {
      if (value === undefined || value === null) return;

      if (Array.isArray(value)) {
        value.forEach((item) => appendQueryParam(key, item));
        return;
      }

      if (typeof value === 'object') {
        Object.entries(value).forEach(([nestedKey, nestedValue]) => {
          appendQueryParam(`${key}[${nestedKey}]`, nestedValue);
        });
        return;
      }

      usp.append(key, String(value));
    };

    Object.entries(query).forEach(([k, v]) => appendQueryParam(k, v));
    const s = usp.toString();
    return s ? `?${s}` : '';
  };

  const url = `${API_ROOT}/${path}${toQs(query)}`;

  const res = await fetch(url, {
    method,
    headers: { ...defaultHeaders, ...(headers ?? {}) },
    body: method === 'GET' ? undefined : JSON.stringify(body ?? {}),
  });

  // The server gates every /api/* route on the session cookie. A 401 means the
  // session expired or was never established, so restart the login flow rather
  // than surfacing a broken panel.
  if (res.status === 401) {
    clearAuthData();
    window.location.href = '/login';
    throw new ApiHttpError(401, 'Session expired', res.statusText);
  }

  let data: unknown;

  try {
    data = await res.json();
  } catch {
    const message = !res.ok ? `${res.status} ${res.statusText}` : 'Failed to parse response as JSON';

    // TODO: Decide if we want to conditionally throw
    console.error(message);
  }

  if (!res.ok) {
    let message = res.statusText;
    try {
      if (data && typeof data === 'object') {
        const errorData = data as { detail?: string; message?: string };
        message = errorData.detail || errorData.message || message;
      }
      /* c8 ignore start */
    } catch {
      /* noop */
    }
    /* c8 ignore stop */

    throw new ApiHttpError(res.status, message, res.statusText, data);
  }

  return data as T;
}
